/**
 * Package Service
 * Handles CRUD operations for destination packages linked to categories
 */

import { supabase } from './supabaseService';

export interface Package {
  id: string;
  categoryId: string;
  name: string;
  slug: string;
  description?: string;
  shortDescription?: string;
  duration?: string;
  price?: number;
  currency?: string;
  difficulty?: string;
  images?: string[];
  highlights?: string[];
  itinerary?: any[];
  includes?: string[];
  excludes?: string[];
  requirements?: string[];
  maxParticipants?: number;
  minParticipants?: number;
  rating?: number;
  reviewCount?: number;
  location?: any;
  guide?: any;
  availability?: any[];
  active: boolean;
  featured: boolean;
  sortOrder?: number;
  createdAt: string;
  updatedAt: string;
}

class PackageService {
  /**
   * Get all packages
   */
  async getAllPackages(): Promise<Package[]> {
    try {
      const { data, error } = await supabase
        .from('packages')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching packages:', error);
        return [];
      }

      return (data || []).map(this.mapDbToPackage);
    } catch (error) {
      console.error('Error in getAllPackages:', error);
      return [];
    }
  }

  /**
   * Get active packages only
   */
  async getActivePackages(): Promise<Package[]> {
    try {
      const { data, error } = await supabase
        .from('packages')
        .select('*')
        .eq('active', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching active packages:', error);
        return [];
      }

      return (data || []).map(this.mapDbToPackage);
    } catch (error) {
      console.error('Error in getActivePackages:', error);
      return [];
    }
  }

  /**
   * Get featured packages
   */
  async getFeaturedPackages(): Promise<Package[]> {
    try {
      const { data, error } = await supabase
        .from('packages')
        .select('*')
        .eq('active', true)
        .eq('featured', true)
        .order('sort_order', { ascending: true })
        .limit(6);

      if (error) {
        console.error('Error fetching featured packages:', error);
        return [];
      }

      return (data || []).map(this.mapDbToPackage);
    } catch (error) {
      console.error('Error in getFeaturedPackages:', error);
      return [];
    }
  }

  /**
   * Get packages by category
   */
  async getPackagesByCategory(categoryId: string): Promise<Package[]> {
    try {
      const { data, error } = await supabase
        .from('packages')
        .select('*')
        .eq('category_id', categoryId)
        .eq('active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching packages by category:', error);
        return [];
      }

      return (data || []).map(this.mapDbToPackage);
    } catch (error) {
      console.error('Error in getPackagesByCategory:', error);
      return [];
    }
  }

  /**
   * Get package by ID
   */
  async getPackageById(id: string): Promise<Package | null> {
    try {
      const { data, error } = await supabase
        .from('packages')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching package:', error);
        return null;
      }

      return data ? this.mapDbToPackage(data) : null;
    } catch (error) {
      console.error('Error in getPackageById:', error);
      return null;
    }
  }

  /**
   * Get package by slug
   */
  async getPackageBySlug(slug: string): Promise<Package | null> {
    try {
      const { data, error } = await supabase
        .from('packages')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) {
        console.error('Error fetching package by slug:', error);
        return null;
      }

      return data ? this.mapDbToPackage(data) : null;
    } catch (error) {
      console.error('Error in getPackageBySlug:', error);
      return null;
    }
  }

  /**
   * Create a new package
   */
  async createPackage(packageData: Omit<Package, 'id' | 'createdAt' | 'updatedAt'>): Promise<Package> {
    try {
      const dbPackage = this.mapPackageToDb(packageData);
      
      const { data, error } = await supabase
        .from('packages')
        .insert([dbPackage])
        .select()
        .single();

      if (error) {
        console.error('Error creating package:', error);
        throw error;
      }

      return this.mapDbToPackage(data);
    } catch (error) {
      console.error('Error in createPackage:', error);
      throw error;
    }
  }

  /**
   * Update a package
   */
  async updatePackage(id: string, updates: Partial<Omit<Package, 'id' | 'createdAt'>>): Promise<Package | null> {
    try {
      const dbUpdates = this.mapPackageToDb(updates as Package, true);
      dbUpdates.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('packages')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating package:', error);
        return null;
      }

      return data ? this.mapDbToPackage(data) : null;
    } catch (error) {
      console.error('Error in updatePackage:', error);
      return null;
    }
  }

  /**
   * Delete a package
   */
  async deletePackage(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('packages')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting package:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deletePackage:', error);
      return false;
    }
  }

  /**
   * Generate slug from name
   */
  generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  /**
   * Map database format to Package interface
   */
  private mapDbToPackage(dbPackage: any): Package {
    return {
      id: dbPackage.id,
      categoryId: dbPackage.category_id,
      name: dbPackage.name,
      slug: dbPackage.slug,
      description: dbPackage.description,
      shortDescription: dbPackage.short_description,
      duration: dbPackage.duration,
      price: dbPackage.price,
      currency: dbPackage.currency,
      difficulty: dbPackage.difficulty,
      images: dbPackage.images || [],
      highlights: dbPackage.highlights || [],
      itinerary: dbPackage.itinerary || [],
      includes: dbPackage.includes || [],
      excludes: dbPackage.excludes || [],
      requirements: dbPackage.requirements || [],
      maxParticipants: dbPackage.max_participants,
      minParticipants: dbPackage.min_participants,
      rating: dbPackage.rating,
      reviewCount: dbPackage.review_count,
      location: dbPackage.location,
      guide: dbPackage.guide,
      availability: dbPackage.availability || [],
      active: dbPackage.active,
      featured: dbPackage.featured,
      sortOrder: dbPackage.sort_order,
      createdAt: dbPackage.created_at,
      updatedAt: dbPackage.updated_at
    };
  }

  /**
   * Map Package interface to database format
   */
  private mapPackageToDb(packageData: Partial<Package>, isUpdate: boolean = false): any {
    const dbPackage: any = {};

    if (packageData.categoryId !== undefined) dbPackage.category_id = packageData.categoryId;
    if (packageData.name !== undefined) dbPackage.name = packageData.name;
    if (packageData.slug !== undefined) dbPackage.slug = packageData.slug;
    if (packageData.description !== undefined) dbPackage.description = packageData.description;
    if (packageData.shortDescription !== undefined) dbPackage.short_description = packageData.shortDescription;
    if (packageData.duration !== undefined) dbPackage.duration = packageData.duration;
    if (packageData.price !== undefined) dbPackage.price = packageData.price;
    if (packageData.currency !== undefined) dbPackage.currency = packageData.currency;
    if (packageData.difficulty !== undefined) dbPackage.difficulty = packageData.difficulty;
    if (packageData.images !== undefined) dbPackage.images = packageData.images;
    if (packageData.highlights !== undefined) dbPackage.highlights = packageData.highlights;
    if (packageData.itinerary !== undefined) dbPackage.itinerary = packageData.itinerary;
    if (packageData.includes !== undefined) dbPackage.includes = packageData.includes;
    if (packageData.excludes !== undefined) dbPackage.excludes = packageData.excludes;
    if (packageData.requirements !== undefined) dbPackage.requirements = packageData.requirements;
    if (packageData.maxParticipants !== undefined) dbPackage.max_participants = packageData.maxParticipants;
    if (packageData.minParticipants !== undefined) dbPackage.min_participants = packageData.minParticipants;
    if (packageData.rating !== undefined) dbPackage.rating = packageData.rating;
    if (packageData.reviewCount !== undefined) dbPackage.review_count = packageData.reviewCount;
    if (packageData.location !== undefined) dbPackage.location = packageData.location;
    if (packageData.guide !== undefined) dbPackage.guide = packageData.guide;
    if (packageData.availability !== undefined) dbPackage.availability = packageData.availability;
    if (packageData.active !== undefined) dbPackage.active = packageData.active;
    if (packageData.featured !== undefined) dbPackage.featured = packageData.featured;
    if (packageData.sortOrder !== undefined) dbPackage.sort_order = packageData.sortOrder;

    return dbPackage;
  }
}

export const packageService = new PackageService();

